import { useState, useEffect } from 'react';
import api from '../services/api';
import tokenDistributionApi from '../services/tokenDistributionApi';
import tokenDistributionWS from '../services/tokenDistributionWebSocket';
import tokenMetadata from '../services/tokenMetadata';
import { useToken } from '../contexts/TokenContext';

const TokenDistributionPage = () => {
  const { tokenId } = useToken();
  const [activeTab, setActiveTab] = useState('distribute');
  const [walletGroups, setWalletGroups] = useState([]);
  const [selectedSourceWallet, setSelectedSourceWallet] = useState(null);
  const [selectedDestWallet, setSelectedDestWallet] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [gathers, setGathers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [distributionResult, setDistributionResult] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [distributeMode, setDistributeMode] = useState('equal'); // equal, custom, random
  const [gatherMode, setGatherMode] = useState('all'); // all, custom
  const [totalAmount, setTotalAmount] = useState('');
  const [randomVariance, setRandomVariance] = useState(20); // Default 20% variance
  const [processSteps, setProcessSteps] = useState([]);
  const [, setCurrentStep] = useState(null);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenBalances, setTokenBalances] = useState({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [expandedTokens, setExpandedTokens] = useState({}); // Track which wallets have expanded token lists
  const [tokenMetadataCache, setTokenMetadataCache] = useState({}); // Cache token metadata
  const [allUserTokens, setAllUserTokens] = useState([]); // All unique tokens the user owns

  useEffect(() => {
    if (tokenId) {
      loadWalletsWithGroups();
    }
  }, [tokenId]);

  // Get all wallets from groups
  const wallets = walletGroups.flatMap(group => group.wallets);

  // Load all token balances when wallets are loaded
  useEffect(() => {
    if (wallets.length > 0) {
      loadAllTokenBalances();
    }
  }, [wallets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load specific token balance when token address changes
  useEffect(() => {
    if (tokenAddress && wallets.length > 0) {
      loadSpecificTokenBalances();
    }
  }, [tokenAddress, wallets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWalletsWithGroups = async () => {
    try {
      const response = await api.get('/solana-distribution/wallets-grouped', {
        params: { token_id: tokenId }
      });
      if (response.data.success) {
        console.log('Wallet groups received:', response.data.data.groups);
        setWalletGroups(response.data.data.groups);
        // Expand small groups by default
        const expanded = {};
        response.data.data.groups.forEach(group => {
          expanded[group.id] = group.wallets.length <= 12;
        });
        setExpandedGroups(expanded);
      }
    } catch (error) {
      console.error('Failed to load wallets:', error);
      setError('Failed to load wallets');
    }
  };

  const loadAllTokenBalances = async () => {
    if (wallets.length === 0) return;

    setBalancesLoading(true);
    try {
      // Load full wallet balances (including all tokens) via existing endpoint
      const walletIds = wallets.map(w => w.id || w._id);
      const response = await api.post('/wallet/balances', { walletIds });
      
      if (response.data.balances) {
        const balanceMap = {};
        response.data.balances.forEach((balance) => {
          const walletId = balance.walletId;
          if (walletId) {
            balanceMap[walletId] = {
              sol: balance.sol?.balance || 0,
              tokens: balance.tokens || [],
              error: balance.error
            };
          }
        });
        setTokenBalances(balanceMap);
        
        // Collect all unique tokens with their total balances
        const tokenMap = new Map();
        Object.values(balanceMap).forEach(balance => {
          if (balance.tokens) {
            balance.tokens.forEach(token => {
              if (tokenMap.has(token.mint)) {
                tokenMap.get(token.mint).totalBalance += parseFloat(token.amount) || 0;
                tokenMap.get(token.mint).walletCount += 1;
              } else {
                tokenMap.set(token.mint, {
                  mint: token.mint,
                  totalBalance: parseFloat(token.amount) || 0,
                  walletCount: 1,
                  decimals: token.decimals
                });
              }
            });
          }
        });
        
        // Convert to array and sort by total balance
        const userTokens = Array.from(tokenMap.values())
          .sort((a, b) => b.totalBalance - a.totalBalance);
        
        setAllUserTokens(userTokens);
        
        // Fetch metadata for all unique tokens
        const uniqueMints = userTokens.map(t => t.mint);
        fetchTokenMetadataBatch(uniqueMints);
      }
    } catch (error) {
      console.error('Error loading balances:', error);
    } finally {
      setBalancesLoading(false);
    }
  };

  const loadSpecificTokenBalances = async () => {
    if (!tokenAddress || wallets.length === 0) return;

    try {
      const walletAddresses = wallets.map(w => w.publicKey);
      const response = await tokenDistributionApi.getMultipleTokenBalances(walletAddresses, tokenAddress);
      
      if (response.success) {
        const newBalances = { ...tokenBalances };
        
        response.balances.forEach(balanceInfo => {
          const wallet = wallets.find(w => w.publicKey === balanceInfo.walletAddress);
          if (wallet) {
            const walletId = wallet.id || wallet._id;
            if (!newBalances[walletId]) {
              newBalances[walletId] = { sol: 0, tokens: [] };
            }
            
            // Update or add the specific token balance
            const existingTokenIndex = newBalances[walletId].tokens.findIndex(t => t.mint === tokenAddress);
            if (existingTokenIndex >= 0) {
              newBalances[walletId].tokens[existingTokenIndex] = {
                mint: tokenAddress,
                amount: balanceInfo.balance,
                decimals: balanceInfo.decimals
              };
            } else {
              newBalances[walletId].tokens.push({
                mint: tokenAddress,
                amount: balanceInfo.balance,
                decimals: balanceInfo.decimals
              });
            }
          }
        });
        
        setTokenBalances(newBalances);
      }
    } catch (error) {
      console.error('Error loading specific token balances:', error);
    }
  };

  const fetchTokenMetadataBatch = async (mintAddresses) => {
    const newMetadata = { ...tokenMetadataCache };
    let hasUpdates = false;
    
    for (const mint of mintAddresses) {
      if (!newMetadata[mint]) {
        try {
          const metadata = await tokenMetadata.getTokenMetadata(mint);
          newMetadata[mint] = metadata;
          hasUpdates = true;
        } catch (error) {
          console.error(`Failed to fetch metadata for ${mint}:`, error);
        }
      }
    }
    
    if (hasUpdates) {
      setTokenMetadataCache(newMetadata);
    }
  };

  const handleSourceWalletSelect = (wallet) => {
    setSelectedSourceWallet(wallet);
    setError(null); // Clear any existing errors
    // Remove from distributions if it was there
    setDistributions(distributions.filter(d => d.wallet.id !== wallet.id));
  };

  const handleDestWalletSelect = (wallet) => {
    setSelectedDestWallet(wallet);
    setError(null); // Clear any existing errors
    // Remove from gathers if it was there
    setGathers(gathers.filter(g => g.wallet.id !== wallet.id));
  };

  const addToDistributions = (wallet) => {
    if (!distributions.find(d => d.wallet.id === wallet.id) && 
        (!selectedSourceWallet || wallet.id !== selectedSourceWallet.id)) {
      setDistributions([...distributions, { 
        wallet, 
        amount: '',
        walletId: wallet.id || wallet._id 
      }]);
      setNeedsRecalculation(true);
    }
  };

  const addToGathers = (wallet) => {
    if (!gathers.find(g => g.wallet.id === wallet.id) && 
        (!selectedDestWallet || wallet.id !== selectedDestWallet.id)) {
      
      const walletId = wallet.id || wallet._id;
      const balance = tokenAddress && tokenBalances[walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0;
      
      setGathers([...gathers, { 
        wallet, 
        amount: balance,
        walletId: walletId
      }]);
    }
  };

  const removeFromDistributions = (walletId) => {
    setDistributions(distributions.filter(d => d.wallet.id !== walletId));
    setNeedsRecalculation(true);
  };

  const removeFromGathers = (walletId) => {
    setGathers(gathers.filter(g => g.wallet.id !== walletId));
  };

  const updateDistributionAmount = (walletId, amount) => {
    setDistributions(distributions.map(d => 
      d.wallet.id === walletId ? { ...d, amount } : d
    ));
  };

  const updateGatherAmount = (walletId, amount) => {
    setGathers(gathers.map(g => 
      g.wallet.id === walletId ? { ...g, amount } : g
    ));
  };

  // Select All functionality
  const toggleSelectAllInGroup = (groupId) => {
    const group = walletGroups.find(g => g.id === groupId);
    if (!group) return;

    // Clear any previous errors
    setError(null);

    // If no source/destination wallet selected, do nothing or show message
    if (activeTab === 'distribute' && !selectedSourceWallet) {
      console.log('DEBUG: toggleSelectAllInGroup - No source wallet selected');
      console.log('DEBUG: selectedSourceWallet:', selectedSourceWallet);
      console.log('DEBUG: activeTab:', activeTab);
      setError('Please select a source wallet first');
      return;
    }
    if (activeTab === 'gather' && !selectedDestWallet) {
      console.log('DEBUG: toggleSelectAllInGroup - No destination wallet selected');
      console.log('DEBUG: selectedDestWallet:', selectedDestWallet);
      console.log('DEBUG: activeTab:', activeTab);
      setError('Please select a destination wallet first');
      return;
    }

    console.log('DEBUG: toggleSelectAllInGroup - Starting with valid wallet selection');
    console.log('DEBUG: selectedSourceWallet:', selectedSourceWallet);
    console.log('DEBUG: selectedDestWallet:', selectedDestWallet);

    let eligibleWallets = [];
    
    if (activeTab === 'distribute') {
      // For distribute: exclude source wallet only (destination wallets don't need to have the token)
      eligibleWallets = group.wallets.filter(w => {
        if (selectedSourceWallet && w.id === selectedSourceWallet.id) return false;
        return true; // All other wallets are eligible for distribution
      });
    } else {
      // For gather: exclude destination wallet and wallets already in gathers
      eligibleWallets = group.wallets.filter(w => {
        if (selectedDestWallet && w.id === selectedDestWallet.id) return false;
        if (!tokenAddress) return true; // If no token selected, all wallets are eligible
        
        // Check if wallet has the selected token
        const walletId = w.id || w._id;
        const balance = tokenBalances[walletId]?.tokens?.find(t => t.mint === tokenAddress);
        return balance && parseFloat(balance.amount) > 0;
      });
    }

    console.log(`[DEBUG] toggleSelectAllInGroup - Group: ${group.name}, Eligible wallets:`, eligibleWallets.length);

    // Check if all eligible wallets are already selected
    const currentList = activeTab === 'distribute' ? distributions : gathers;
    const selectedWalletIds = currentList.map(item => item.wallet.id);
    const eligibleWalletIds = eligibleWallets.map(w => w.id);
    const selectedEligibleWallets = eligibleWalletIds.filter(id => selectedWalletIds.includes(id));
    
    const isAllSelected = selectedEligibleWallets.length === eligibleWalletIds.length && eligibleWalletIds.length > 0;
    
    console.log(`[DEBUG] isAllSelected:`, isAllSelected);

    if (isAllSelected) {
      // Unselect all wallets in this group
      if (activeTab === 'distribute') {
        setDistributions(distributions.filter(d => !eligibleWalletIds.includes(d.wallet.id)));
        setNeedsRecalculation(true);
      } else {
        setGathers(gathers.filter(g => !eligibleWalletIds.includes(g.wallet.id)));
      }
    } else {
      // Select all eligible wallets in this group
      const walletsToAdd = eligibleWallets.filter(w => !selectedWalletIds.includes(w.id));
      
      if (activeTab === 'distribute') {
        const newDistributions = walletsToAdd.map(wallet => ({
          wallet,
          amount: '',
          walletId: wallet.id || wallet._id
        }));
        setDistributions([...distributions, ...newDistributions]);
        setNeedsRecalculation(true);
      } else {
        const newGathers = walletsToAdd.map(wallet => {
          const walletId = wallet.id || wallet._id;
          const balance = tokenAddress && tokenBalances[walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0;
          return {
            wallet,
            amount: balance,
            walletId: walletId
          };
        });
        setGathers([...gathers, ...newGathers]);
      }
    }
  };

  const getSelectAllButtonText = (groupId) => {
    const group = walletGroups.find(g => g.id === groupId);
    if (!group) return 'Select All';

    // If no source/dest wallet selected, show generic text
    if (activeTab === 'distribute' && !selectedSourceWallet) {
      return 'Select All';
    }
    if (activeTab === 'gather' && !selectedDestWallet) {
      return 'Select All';
    }

    let eligibleWallets = [];
    
    if (activeTab === 'distribute') {
      eligibleWallets = group.wallets.filter(w => {
        if (selectedSourceWallet && w.id === selectedSourceWallet.id) return false;
        return true; // All other wallets are eligible for distribution
      });
    } else {
      eligibleWallets = group.wallets.filter(w => {
        if (selectedDestWallet && w.id === selectedDestWallet.id) return false;
        if (!tokenAddress) return true;
        
        const walletId = w.id || w._id;
        const balance = tokenBalances[walletId]?.tokens?.find(t => t.mint === tokenAddress);
        return balance && parseFloat(balance.amount) > 0;
      });
    }

    if (eligibleWallets.length === 0) {
      return 'No Eligible Wallets';
    }

    const currentList = activeTab === 'distribute' ? distributions : gathers;
    const selectedWalletIds = currentList.map(item => item.wallet.id);
    const eligibleWalletIds = eligibleWallets.map(w => w.id);
    const selectedCount = eligibleWalletIds.filter(id => selectedWalletIds.includes(id)).length;

    if (selectedCount === eligibleWalletIds.length && eligibleWalletIds.length > 0) {
      return 'Unselect All';
    } else if (selectedCount > 0) {
      return `Select All (${selectedCount}/${eligibleWallets.length})`;
    } else {
      return 'Select All';
    }
  };

  const calculateDistributions = () => {
    if (!totalAmount || parseFloat(totalAmount) <= 0 || distributions.length === 0) {
      setError('Please enter a valid total amount and have at least one destination wallet');
      return;
    }

    const total = parseFloat(totalAmount);
    const count = distributions.length;

    let updatedDistributions;

    if (distributeMode === 'equal') {
      const amountPerWallet = (total / count).toFixed(4);
      updatedDistributions = distributions.map(d => ({
        ...d,
        amount: amountPerWallet
      }));
    } else if (distributeMode === 'random') {
      // Random distribution with variance
      const baseAmount = total / count;
      const variance = randomVariance / 100;
      let remainingAmount = total;
      
      updatedDistributions = distributions.map((d, index) => {
        if (index === distributions.length - 1) {
          // Last wallet gets the remaining amount to ensure exact total
          return { ...d, amount: remainingAmount.toFixed(4) };
        }
        
        // Random amount within variance range
        const minAmount = baseAmount * (1 - variance);
        const maxAmount = baseAmount * (1 + variance);
        const randomAmount = minAmount + Math.random() * (maxAmount - minAmount);
        const amount = Math.min(randomAmount, remainingAmount);
        
        remainingAmount -= amount;
        return { ...d, amount: amount.toFixed(4) };
      });
    }

    if (updatedDistributions) {
      setDistributions(updatedDistributions);
      setNeedsRecalculation(false);
    }
  };

  const handleTokenSelect = (mint) => {
    setTokenAddress(mint);
  };

  const initializeSteps = (type, count) => {
    const steps = [
      {
        id: 'validation',
        name: 'Validating Request',
        description: 'Checking wallets and amounts',
        status: 'pending'
      },
      {
        id: 'processing',
        name: type === 'distribute' ? 'Distributing Tokens' : 'Gathering Tokens',
        description: `Processing ${count} transfers`,
        status: 'pending'
      },
      {
        id: 'completion',
        name: 'Completing Transaction',
        description: 'Processing results and updating balances',
        status: 'pending'
      }
    ];
    setProcessSteps(steps);
    setCurrentStep('validation');
  };

  const updateStepStatus = (stepId, status, description = null) => {
    setProcessSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status, ...(description && { description }) }
        : step
    ));
  };

  const executeDistribution = async () => {
    if (!selectedSourceWallet || distributions.length === 0 || !tokenAddress) {
      setError('Please select a source wallet, token address, and at least one destination');
      return;
    }

    if ((distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation) {
      setError('Please click "Calculate" to update amounts before distributing');
      return;
    }

    const totalToDistribute = distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    if (totalToDistribute <= 0) {
      setError('Please enter positive amounts for distribution');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setDistributionResult(null);
    
    initializeSteps('distribute', distributions.length);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Step 1: Validation
      updateStepStatus('validation', 'loading', 'Preparing distribution request...');
      
      // Prepare distribution data
      const targetWallets = distributions.map(d => d.wallet.publicKey);
      const customAmounts = {};
      distributions.forEach(d => {
        // Round to 6 decimal places to prevent floating-point precision errors
        customAmounts[d.wallet.publicKey] = Math.round((parseFloat(d.amount) || 0) * 1000000) / 1000000;
      });

      // Round total amount to prevent floating-point precision errors
      const roundedTotalToDistribute = Math.round(totalToDistribute * 1000000) / 1000000;

      updateStepStatus('validation', 'completed', 'Request prepared - starting distribution');
      setCurrentStep('processing');
      updateStepStatus('processing', 'loading', `Sending distribution request to external API...`);

      console.log('Sending distribution request:', {
        tokenAddress,
        sourceWallet: selectedSourceWallet.publicKey,
        targetWallets,
        totalAmount: roundedTotalToDistribute,
        customAmounts
      });

      // Send distribution request to external API
      const response = await tokenDistributionApi.distributeTokens(
        tokenAddress,
        selectedSourceWallet.publicKey,
        targetWallets,
        roundedTotalToDistribute,
        0, // variance (0 for custom amounts)
        customAmounts
      );

      if (response.status === 'success') {
        const taskId = response.task_id;
        setActiveTaskId(taskId);
        
        updateStepStatus('processing', 'loading', 'Connecting to transfer monitor...');
        
        // Connect WebSocket to monitor progress
        await tokenDistributionWS.connectToTask(taskId);
        
        // Subscribe to task updates
        tokenDistributionWS.subscribeToTask(taskId, (data) => {
          console.log('Distribution update:', data);
          
          if (data.type === 'transfer_progress') {
            const progress = `Processing: ${data.success}/${data.total} successful, ${data.failed} failed`;
            updateStepStatus('processing', 'loading', progress);
          } else if (data.type === 'transfer_complete') {
            handleDistributionComplete(data);
          } else if (data.type === 'transfer_error') {
            handleDistributionError(data);
          }
        });
        
        setSuccessMessage(`Distribution started! Task ID: ${taskId}`);
      } else {
        throw new Error(response.message || 'Failed to start distribution');
      }

    } catch (error) {
      console.error('Distribution error:', error);
      updateStepStatus('completion', 'error', error.message || 'Distribution failed');
      setError(error.message || 'Distribution failed');
      setIsLoading(false);
    }
  };

  const handleDistributionComplete = async (data) => {
    updateStepStatus('processing', 'completed', 'All transfers completed');
    setCurrentStep('completion');
    updateStepStatus('completion', 'loading', 'Updating balances...');
    
    // Update operation status in database
    if (data.task_id) {
      try {
        await api.put(`/token-operations/task/${data.task_id}/status`, {
          status: 'completed',
          metadata: {
            successfulTransfers: data.success || 0,
            failedTransfers: data.failed || 0,
            completedAt: new Date().toISOString()
          }
        });
        console.log(`Updated operation status to completed for task ${data.task_id}`);
      } catch (error) {
        console.error('Failed to update operation status:', error);
      }
    }
    
    setDistributionResult({
      summary: {
        totalDistributed: distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0),
        successfulTransfers: data.success || 0,
        failedTransfers: data.failed || 0
      },
      errors: []
    });

    if ((data.success || 0) > 0) {
      setDistributions([]);
      setTotalAmount('');
      loadAllTokenBalances(); // Reload balances
    }

    updateStepStatus('completion', 'completed', 'Distribution completed');
    setIsLoading(false);
    setActiveTaskId(null);
    tokenDistributionWS.unsubscribeFromTask(data.task_id);
    
    setTimeout(() => {
      setProcessSteps([]);
      setCurrentStep(null);
    }, 3000);
  };

  const handleDistributionError = async (data) => {
    updateStepStatus('processing', 'completed', 'Distribution completed with some failures');
    setCurrentStep('completion');
    updateStepStatus('completion', 'completed', 'Distribution completed');
    
    // Update operation status in database
    if (data.task_id) {
      try {
        const status = (data.success || 0) > 0 ? 'completed' : 'failed';
        await api.put(`/token-operations/task/${data.task_id}/status`, {
          status: status,
          metadata: {
            successfulTransfers: data.success || 0,
            failedTransfers: data.failed || 0,
            completedAt: new Date().toISOString(),
            hasErrors: true
          }
        });
        console.log(`Updated operation status to ${status} for task ${data.task_id}`);
      } catch (error) {
        console.error('Failed to update operation status:', error);
      }
    }
    
    // Show results even if some failed
    setDistributionResult({
      summary: {
        totalDistributed: distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0),
        successfulTransfers: data.success || 0,
        failedTransfers: data.failed || 0
      },
      errors: []
    });

    // Only reload balances if some succeeded
    if ((data.success || 0) > 0) {
      setDistributions([]);
      setTotalAmount('');
      loadAllTokenBalances(); // Reload balances
    }

    setIsLoading(false);
    setActiveTaskId(null);
    tokenDistributionWS.unsubscribeFromTask(data.task_id);
    
    // Clear steps after delay
    setTimeout(() => {
      setProcessSteps([]);
      setCurrentStep(null);
    }, 3000);
  };

  const executeGather = async () => {
    if (!selectedDestWallet || gathers.length === 0 || !tokenAddress) {
      setError('Please select a destination wallet, token address, and at least one source');
      return;
    }

    if (gatherMode === 'custom') {
      const totalToGather = gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
      if (totalToGather <= 0) {
        setError('Please enter positive amounts for gathering');
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setDistributionResult(null);
    
    initializeSteps('gather', gathers.length);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Step 1: Validation
      updateStepStatus('validation', 'loading', 'Preparing gather request...');
      
      // Prepare gather data
      const sourceWallets = gathers.map(g => g.wallet.publicKey);
      const customAmounts = {};
      
      // Always populate custom amounts - either from user input or wallet balances
      gathers.forEach(g => {
        if (gatherMode === 'custom') {
          // Use user-specified custom amount, rounded to 6 decimal places
          customAmounts[g.wallet.publicKey] = Math.round((parseFloat(g.amount) || 0) * 1000000) / 1000000;
        } else {
          // Use actual wallet balance for 'all' mode, rounded to 6 decimal places
          const walletBalance = tokenBalances[g.walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0;
          customAmounts[g.wallet.publicKey] = Math.round((parseFloat(walletBalance) || 0) * 1000000) / 1000000;
        }
      });

      // Calculate total amount with proper rounding to prevent floating-point precision errors
      const totalAmount = gatherMode === 'all' 
        ? Math.round(gathers.reduce((sum, g) => sum + (tokenBalances[g.walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0), 0) * 1000000) / 1000000
        : Math.round(gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0) * 1000000) / 1000000;

      updateStepStatus('validation', 'completed', 'Request prepared - starting gather');
      setCurrentStep('processing');
      updateStepStatus('processing', 'loading', `Sending gather request to external API...`);

      console.log('Sending gather request:', {
        tokenAddress,
        sourceWallets,
        destWallet: selectedDestWallet.publicKey,
        totalAmount,
        customAmounts
      });

      // Send gather request to external API
      const response = await tokenDistributionApi.gatherTokens(
        tokenAddress,
        sourceWallets,
        selectedDestWallet.publicKey,
        totalAmount,
        customAmounts // Always send custom amounts (populated with wallet balances or user input)
      );

      if (response.status === 'success') {
        const taskId = response.task_id;
        setActiveTaskId(taskId);
        
        updateStepStatus('processing', 'loading', 'Connecting to transfer monitor...');
        
        // Connect WebSocket to monitor progress
        await tokenDistributionWS.connectToTask(taskId);
        
        // Subscribe to task updates
        tokenDistributionWS.subscribeToTask(taskId, (data) => {
          console.log('Gather update:', data);
          
          if (data.type === 'transfer_progress') {
            const progress = `Processing: ${data.success}/${data.total} successful, ${data.failed} failed`;
            updateStepStatus('processing', 'loading', progress);
          } else if (data.type === 'transfer_complete') {
            handleGatherComplete(data);
          } else if (data.type === 'transfer_error') {
            handleGatherError(data);
          }
        });
        
        setSuccessMessage(`Gather started! Task ID: ${taskId}`);
      } else {
        throw new Error(response.message || 'Failed to start gather');
      }

    } catch (error) {
      console.error('Gather error:', error);
      updateStepStatus('processing', 'error', 'Gather failed');
      setError(error.message || 'Gather failed');
      setIsLoading(false);
    }
  };

  const handleGatherComplete = async (data) => {
    updateStepStatus('processing', 'completed', 'All gather operations completed');
    setCurrentStep('completion');
    updateStepStatus('completion', 'loading', 'Updating balances...');
    
    // Update operation status in database
    if (data.task_id) {
      try {
        await api.put(`/token-operations/task/${data.task_id}/status`, {
          status: 'completed',
          metadata: {
            successfulTransfers: data.success || 0,
            failedTransfers: data.failed || 0,
            completedAt: new Date().toISOString()
          }
        });
        console.log(`Updated gather operation status to completed for task ${data.task_id}`);
      } catch (error) {
        console.error('Failed to update gather operation status:', error);
      }
    }
    
    // Calculate total gathered amount based on mode
    const totalGathered = gatherMode === 'all' 
      ? gathers.reduce((sum, g) => sum + (tokenBalances[g.walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0), 0)
      : gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
    
    setDistributionResult({
      summary: {
        totalGathered: Math.round(totalGathered * 1000000) / 1000000, // Round to 6 decimal places
        successfulTransfers: data.success || 0,
        failedTransfers: data.failed || 0
      },
      errors: []
    });

    if ((data.success || 0) > 0) {
      setGathers([]);
      loadAllTokenBalances(); // Reload balances
    }

    updateStepStatus('completion', 'completed', 'Gather completed');
    setIsLoading(false);
    setActiveTaskId(null);
    tokenDistributionWS.unsubscribeFromTask(data.task_id);
    
    setTimeout(() => {
      setProcessSteps([]);
      setCurrentStep(null);
    }, 3000);
  };

  const handleGatherError = async (data) => {
    updateStepStatus('processing', 'completed', 'Gather completed with some failures');
    setCurrentStep('completion');
    updateStepStatus('completion', 'completed', 'Gather completed');
    
    // Update operation status in database
    if (data.task_id) {
      try {
        const status = (data.success || 0) > 0 ? 'completed' : 'failed';
        await api.put(`/token-operations/task/${data.task_id}/status`, {
          status: status,
          metadata: {
            successfulTransfers: data.success || 0,
            failedTransfers: data.failed || 0,
            completedAt: new Date().toISOString(),
            hasErrors: true
          }
        });
        console.log(`Updated gather operation status to ${status} for task ${data.task_id}`);
      } catch (error) {
        console.error('Failed to update gather operation status:', error);
      }
    }
    
    // Show results even if some failed
    setDistributionResult({
      summary: {
        totalGathered: gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0),
        successfulTransfers: data.success || 0,
        failedTransfers: data.failed || 0
      },
      errors: []
    });

    // Only reload balances if some succeeded
    if ((data.success || 0) > 0) {
      setGathers([]);
      loadAllTokenBalances(); // Reload balances
    }

    setIsLoading(false);
    setActiveTaskId(null);
    tokenDistributionWS.unsubscribeFromTask(data.task_id);
    
    // Clear steps after delay
    setTimeout(() => {
      setProcessSteps([]);
      setCurrentStep(null);
    }, 3000);
  };

  const copyToClipboard = async (text, e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      console.log('Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const renderWalletCard = (wallet) => {
    const showAllTokens = expandedTokens[wallet.id] || false;
    
    const isSelected = activeTab === 'distribute' 
      ? selectedSourceWallet?.id === wallet.id
      : selectedDestWallet?.id === wallet.id;
    
    const isInList = activeTab === 'distribute'
      ? distributions.find(d => d.wallet.id === wallet.id)
      : gathers.find(g => g.wallet.id === wallet.id);
    
    const walletId = wallet.id || wallet._id;
    const balance = tokenBalances[walletId];
    const tokens = balance?.tokens || [];
    
    // Sort tokens by balance (highest first)
    const sortedTokens = [...tokens].sort((a, b) => (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0));
    
    // Decide which tokens to show
    const displayTokens = showAllTokens ? sortedTokens : sortedTokens.slice(0, 3);
    const hiddenTokenCount = sortedTokens.length - displayTokens.length;
    
    return (
      <div 
        key={wallet.id}
        className={`relative p-2 border rounded cursor-pointer transition-all hover:shadow-sm ${
          isSelected 
            ? 'border-green-500 bg-green-50 dark:bg-green-900/30' 
            : isInList
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
        }`}
        onClick={() => {
          if (activeTab === 'distribute') {
            if (isSelected) {
              setSelectedSourceWallet(null);
            } else if (isInList) {
              removeFromDistributions(wallet.id);
            } else {
              if (!selectedSourceWallet) {
                handleSourceWalletSelect(wallet);
              } else {
                addToDistributions(wallet);
              }
            }
          } else {
            if (isSelected) {
              setSelectedDestWallet(null);
            } else if (isInList) {
              removeFromGathers(wallet.id);
            } else {
              if (!selectedDestWallet) {
                handleDestWalletSelect(wallet);
              } else {
                addToGathers(wallet);
              }
            }
          }
        }}
      >
        <div className="space-y-1">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm transition-colors">{wallet.name}</h4>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate flex-1 transition-colors">
              {wallet.publicKey ? `${wallet.publicKey.slice(0, 6)}...${wallet.publicKey.slice(-6)}` : 'No address'}
            </p>
            <button
              onClick={(e) => copyToClipboard(wallet.publicKey || '', e)}
              className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Copy address"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
          
          {/* SOL Balance */}
          <p className="text-sm font-semibold text-green-600 dark:text-green-400 transition-colors">
            {balance?.sol !== undefined ? balance.sol.toFixed(4) : '0.0000'} SOL
          </p>
          
          {/* Token Balances */}
          {displayTokens.map(token => {
            const metadata = tokenMetadataCache[token.mint];
            const symbol = metadata?.symbol || `${token.mint.slice(0, 4)}...`;
            const isSelectedToken = tokenAddress === token.mint;
            const amount = parseFloat(token.amount) || 0;
            
            // Format large numbers better
            const formatAmount = (num) => {
              if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
              if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
              return num.toFixed(4);
            };
            
            return (
              <div 
                key={token.mint} 
                className={`flex justify-between items-center text-xs ${
                  isSelectedToken ? 'bg-yellow-100 dark:bg-yellow-900/30 -mx-1 px-1 rounded' : ''
                }`}
              >
                <span 
                  className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer underline decoration-dotted truncate mr-1 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTokenSelect(token.mint);
                  }}
                  title={`Click to select ${metadata?.name || 'this token'}`}
                >
                  {symbol}
                </span>
                <span className="font-medium text-right text-gray-900 dark:text-gray-100 transition-colors">
                  {formatAmount(amount)}
                </span>
              </div>
            );
          })}
          
          {/* Show more/less button */}
          {sortedTokens.length > 3 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedTokens(prev => ({
                  ...prev,
                  [wallet.id]: !prev[wallet.id]
                }));
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mt-1 transition-colors"
            >
              {showAllTokens 
                ? 'Show less' 
                : `Show ${hiddenTokenCount} more token${hiddenTokenCount > 1 ? 's' : ''}`
              }
            </button>
          )}
        </div>
        
        {isSelected && (
          <div className="absolute top-1 right-1 px-1 py-0.5 bg-green-600 text-white text-xs font-bold rounded">
            {activeTab === 'distribute' ? 'SRC' : 'DEST'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full p-4 bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1 transition-colors">Token Distribution & Gathering</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm transition-colors">Distribute tokens from one wallet to many, or gather tokens from many wallets to one.</p>
        </div>

      {/* Token Input and Your Tokens Section */}
      <div className="mb-6 space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                Token Address
              </label>
              <input
                type="text"
                value={tokenAddress}
                onChange={(e) => setTokenAddress(e.target.value)}
                placeholder="Enter token mint address"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Your Tokens Section */}
        {allUserTokens.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Your Tokens</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {allUserTokens.map(token => {
                const metadata = tokenMetadataCache[token.mint];
                const symbol = metadata?.symbol || `${token.mint.slice(0, 4)}...`;
                const isSelected = tokenAddress === token.mint;
                
                return (
                  <button
                    key={token.mint}
                    onClick={() => handleTokenSelect(token.mint)}
                    className={`
                      px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isSelected 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span>{symbol}</span>
                      <span className="text-xs opacity-75 ml-2">
                        {token.walletCount} wallet{token.walletCount > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="text-xs mt-1 opacity-75">
                      {token.totalBalance.toFixed(2)} total
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 transition-colors">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 transition-colors">
          {successMessage}
        </div>
      )}

      {/* Process Steps */}
      {processSteps.length > 0 && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors">Transaction Progress</h3>
          <div className="space-y-3">
            {processSteps.map((step) => (
              <div key={step.id} className="flex items-center space-x-3">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${step.status === 'completed' ? 'bg-green-500' : 
                    step.status === 'loading' ? 'bg-blue-500' : 
                    step.status === 'error' ? 'bg-red-500' : 'bg-gray-300'}
                `}>
                  {step.status === 'completed' && (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {step.status === 'loading' && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {step.status === 'error' && (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{step.name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distribution Result */}
      {distributionResult && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors">Transaction Results</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{distributionResult.summary.successfulTransfers}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Successful</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{distributionResult.summary.failedTransfers}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Failed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {(distributionResult.summary.successfulTransfers || 0) + (distributionResult.summary.failedTransfers || 0)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Total Transactions</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg transition-colors">
        <button
          onClick={() => setActiveTab('distribute')}
          className={`
            flex-1 py-2 px-4 rounded-md font-medium transition-all
            ${activeTab === 'distribute' 
              ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }
          `}
        >
          Distribute Tokens
        </button>
        <button
          onClick={() => setActiveTab('gather')}
          className={`
            flex-1 py-2 px-4 rounded-md font-medium transition-all
            ${activeTab === 'gather' 
              ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }
          `}
        >
          Gather Tokens
        </button>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Settings Row - How to Use + Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - How to Use Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6 transition-colors">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 transition-colors">How to use:</h3>
            <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 transition-colors">
              {activeTab === 'distribute' ? (
                // Distribution instructions
                <>
                  <li>1. Enter a token address above (or select from "Your Tokens")</li>
                  <li>2. Select a source wallet (contains tokens to distribute)</li>
                  <li>3. Select destination wallets (click to turn them blue)</li>
                  {distributeMode === 'equal' && (
                    <>
                      <li>4. Enter total amount to distribute</li>
                      <li>5. Click "Calculate" to set equal amounts per wallet</li>
                    </>
                  )}
                  {distributeMode === 'random' && (
                    <>
                      <li>4. Enter total amount to distribute</li>
                      <li>5. Adjust variance slider for randomness (optional)</li>
                      <li>6. Click "Calculate" to set random amounts per wallet</li>
                    </>
                  )}
                  {distributeMode === 'custom' && (
                    <li>4. Enter custom amounts for each destination wallet</li>
                  )}
                  <li>{distributeMode === 'custom' ? '5' : '6'}. Click "Distribute Tokens" to perform the transfers</li>
                </>
              ) : (
                // Gather instructions
                <>
                  <li>1. Enter a token address above (or select from "Your Tokens")</li>
                  <li>2. Select a destination wallet (will receive gathered tokens)</li>
                  <li>3. Select source wallets (contain tokens to gather)</li>
                  {gatherMode === 'all' && (
                    <li>4. All available tokens will be gathered from selected wallets</li>
                  )}
                  {gatherMode === 'custom' && (
                    <li>4. Enter custom amounts to gather from each source wallet</li>
                  )}
                  <li>{gatherMode === 'custom' ? '5' : '4'}. Click "Gather Tokens" to perform the transfers</li>
                </>
              )}
            </ol>
          </div>

          {/* Right - Distribution/Gather Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700 transition-colors">
          {activeTab === 'distribute' ? (
            // Distribution Tab
            <>
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors">Distribution Settings</h2>
              
              {/* Source Wallet */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Source Wallet</h3>
                {selectedSourceWallet ? (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg transition-colors">
                    <p className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{selectedSourceWallet.name}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                      {selectedSourceWallet.publicKey.slice(0, 8)}...{selectedSourceWallet.publicKey.slice(-8)}
                    </p>
                    {tokenAddress && tokenBalances[selectedSourceWallet.id || selectedSourceWallet._id]?.tokens && (
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mt-1 transition-colors">
                        Balance: {
                          tokenBalances[selectedSourceWallet.id || selectedSourceWallet._id].tokens
                            .find(t => t.mint === tokenAddress)?.amount || '0'
                        } tokens
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Click a wallet to select as source</p>
                )}
              </div>

              {/* Distribution Mode */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Distribution Mode</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="equal"
                      checked={distributeMode === 'equal'}
                      onChange={(e) => {
                        setDistributeMode(e.target.value);
                        setNeedsRecalculation(true);
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Equal Distribution</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="custom"
                      checked={distributeMode === 'custom'}
                      onChange={(e) => setDistributeMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Custom Amounts</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="random"
                      checked={distributeMode === 'random'}
                      onChange={(e) => {
                        setDistributeMode(e.target.value);
                        setNeedsRecalculation(true);
                      }}
                      className="mr-2"
                    />
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Random Distribution</span>
                  </label>
                </div>
              </div>

              {/* Total Amount (for equal/random) */}
              {(distributeMode === 'equal' || distributeMode === 'random') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Total Amount to Distribute
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={totalAmount}
                      onChange={(e) => {
                        setTotalAmount(e.target.value);
                        setNeedsRecalculation(true);
                      }}
                      placeholder="Enter total amount"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    />
                    <button
                      onClick={calculateDistributions}
                      disabled={!totalAmount || distributions.length === 0}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Calculate
                    </button>
                  </div>
                </div>
              )}

              {/* Random Variance */}
              {distributeMode === 'random' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Variance (%) - {randomVariance}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={randomVariance}
                    onChange={(e) => {
                      setRandomVariance(parseInt(e.target.value));
                      setNeedsRecalculation(true);
                    }}
                    className="w-full"
                  />
                </div>
              )}

              {/* Destination Wallets */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                  Destination Wallets ({distributions.length})
                </h3>
                {distributions.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {distributions.map((dist) => (
                      <div key={dist.wallet.id} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">{dist.wallet.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
                            {dist.wallet.publicKey.slice(0, 6)}...{dist.wallet.publicKey.slice(-4)}
                          </p>
                        </div>
                        {distributeMode === 'custom' && (
                          <input
                            type="number"
                            value={dist.amount}
                            onChange={(e) => updateDistributionAmount(dist.wallet.id, e.target.value)}
                            placeholder="Amount"
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        )}
                        {(distributeMode === 'equal' || distributeMode === 'random') && (
                          <span className="text-sm font-medium">{dist.amount || '0'}</span>
                        )}
                        <button
                          onClick={() => removeFromDistributions(dist.wallet.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Click wallets to add as destinations</p>
                )}
              </div>

              {/* Total Summary */}
              {distributions.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Total to distribute:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 transition-colors">
                      {distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0).toFixed(4)} tokens
                    </span>
                  </div>
                </div>
              )}

              {/* Distribute Button */}
              <button
                onClick={executeDistribution}
                disabled={isLoading || !selectedSourceWallet || distributions.length === 0 || !tokenAddress}
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {isLoading ? 'Processing...' : 'Distribute Tokens'}
              </button>
            </>
          ) : (
            // Gather Tab
            <>
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors">Gather Settings</h2>
              
              {/* Destination Wallet */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Destination Wallet</h3>
                {selectedDestWallet ? (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg transition-colors">
                    <p className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{selectedDestWallet.name}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                      {selectedDestWallet.publicKey.slice(0, 8)}...{selectedDestWallet.publicKey.slice(-8)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Click a wallet to select as destination</p>
                )}
              </div>

              {/* Gather Mode */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Gather Mode</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="all"
                      checked={gatherMode === 'all'}
                      onChange={(e) => setGatherMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Gather All Tokens</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="custom"
                      checked={gatherMode === 'custom'}
                      onChange={(e) => setGatherMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Custom Amounts</span>
                  </label>
                </div>
              </div>

              {/* Source Wallets */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                  Source Wallets ({gathers.length})
                </h3>
                {gathers.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {gathers.map((gather) => {
                      const balance = tokenAddress && tokenBalances[gather.walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0;
                      
                      return (
                        <div key={gather.wallet.id} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded transition-colors">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">{gather.wallet.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
                              {gather.wallet.publicKey.slice(0, 6)}...{gather.wallet.publicKey.slice(-4)}
                              {tokenAddress && (
                                <span className="ml-2 text-purple-600 dark:text-purple-400 transition-colors">
                                  Balance: {balance}
                                </span>
                              )}
                            </p>
                          </div>
                          {gatherMode === 'custom' && (
                            <input
                              type="number"
                              value={gather.amount}
                              onChange={(e) => updateGatherAmount(gather.wallet.id, e.target.value)}
                              placeholder="Amount"
                              max={balance}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
                            />
                          )}
                          {gatherMode === 'all' && (
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">{balance}</span>
                          )}
                          <button
                            onClick={() => removeFromGathers(gather.wallet.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Click wallets to add as sources</p>
                )}
              </div>

              {/* Total Summary */}
              {gathers.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-900 dark:text-gray-100 transition-colors">Total to gather:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 transition-colors">
                      {gatherMode === 'all'
                        ? gathers.reduce((sum, g) => {
                            const balance = tokenAddress && tokenBalances[g.walletId]?.tokens?.find(t => t.mint === tokenAddress)?.amount || 0;
                            return sum + parseFloat(balance);
                          }, 0).toFixed(4)
                        : gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0).toFixed(4)
                      } tokens
                    </span>
                  </div>
                </div>
              )}

              {/* Gather Button */}
              <button
                onClick={executeGather}
                disabled={isLoading || !selectedDestWallet || gathers.length === 0 || !tokenAddress}
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {isLoading ? 'Processing...' : 'Gather Tokens'}
              </button>
            </>
          )}
          </div>
        </div>

        {/* Full Width Wallet Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors">
            Select Wallets
          </h2>
          
          {!tokenAddress && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-300 transition-colors">
              Please enter a token address first to see token balances
            </div>
          )}
          
          {walletGroups.map(group => (
            <div key={group.id} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden mb-3 transition-colors">
              <div 
                className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => setExpandedGroups({
                  ...expandedGroups,
                  [group.id]: !expandedGroups[group.id]
                })}
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: group.color || '#9333ea' }}
                  ></div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{group.name}</h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">({group.wallets.length} wallets)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      const buttonText = getSelectAllButtonText(group.id);
                      if (buttonText !== 'No Eligible Wallets') {
                        toggleSelectAllInGroup(group.id);
                      }
                    }}
                    className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                      getSelectAllButtonText(group.id) === 'No Eligible Wallets'
                        ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/40'
                    }`}
                    disabled={getSelectAllButtonText(group.id) === 'No Eligible Wallets'}
                  >
                    {getSelectAllButtonText(group.id)}
                  </button>
                  <span className="text-gray-400 dark:text-gray-500 transition-colors">
                    {expandedGroups[group.id] ? '▼' : '▶'}
                  </span>
                </div>
              </div>
              
              {expandedGroups[group.id] && (
                <div className="p-3">
                  <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 ${
                    group.wallets.length > 32 ? 'max-h-64 overflow-y-auto' : ''
                  }`}>
                    {group.wallets.map(wallet => renderWalletCard(wallet))}
                  </div>
                  {group.wallets.length > 32 && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center transition-colors">
                      {group.wallets.length} wallets (scroll to see more)
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default TokenDistributionPage;